import {
  GESTURE_LIST,
  MAX_CHAT_LENGTH,
  gesturesForPrefix,
  type ChatSaidPayload,
  type Gesture,
} from "@clubap/shared";
import { useEffect, useRef, useState } from "react";
import { IconEnviar } from "./icons.js";

interface Props {
  messages: ChatSaidPayload[];
  notice: string | null;
  /** Para distinguir lo propio de lo ajeno de un vistazo. */
  selfSessionId: string | null;
  onSend: (text: string) => void;
  /**
   * El disparador va en la barra de chat y no en el dock.
   *
   * Un gesto es expresión, igual que una línea de chat, y ahí es donde Club Penguin lo
   * puso. El dock son tres fichas por decisión suya —ya carga una cuarta declarada como
   * deuda— así que meterle una quinta sería gastarle el sitio a otra cosa.
   */
  onGesture: (gesture: Gesture) => void;
}

/** Cuánto queda expandido el chat tras el último mensaje, en ms. */
const AUTO_COLLAPSE_MS = 9000;

function timeOf(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function ChatPanel({ messages, notice, selfSessionId, onSend, onGesture }: Props) {
  const [draft, setDraft] = useState("");
  /**
   * El registro arranca plegado y se abre solo cuando hay conversación.
   *
   * En un juego el mundo es el contenido: un panel de chat fijo de 130px tapa
   * pantalla todo el tiempo para algo que la mayor parte del rato está vacío.
   */
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [gestos, setGestos] = useState(false);

  const logRef = useRef<HTMLDivElement>(null);
  const lastCount = useRef(messages.length);

  // Un mensaje nuevo abre el registro; el temporizador lo cierra si nadie escribe.
  useEffect(() => {
    if (messages.length === lastCount.current) return;
    lastCount.current = messages.length;
    setOpen(true);
  }, [messages.length]);

  useEffect(() => {
    // Mientras el campo tiene el foco no se pliega: estarías leyendo o respondiendo.
    if (!open || focused) return;
    const timer = setTimeout(() => setOpen(false), AUTO_COLLAPSE_MS);
    return () => clearTimeout(timer);
  }, [open, focused, messages.length]);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages, open]);

  const expanded = open || focused;
  const unread = !expanded && messages.length > 0;

  /*
   * Escribir una barra abre la tira sola, ya filtrada por lo que se lleve escrito.
   *
   * Los comandos existían y no se veían en ninguna parte: el único modo de enterarse
   * de `/bailar` era abrir la tira con el botón y leer el rótulo de una ficha. La barra
   * es el momento exacto en que alguien está preguntando "¿qué puedo escribir acá?", y
   * es cuando conviene contestarle.
   */
  const sugiriendo = draft.startsWith("/");
  const tira = sugiriendo ? gesturesForPrefix(draft) : gestos ? GESTURE_LIST : [];

  const elegir = (gesture: Gesture): void => {
    onGesture(gesture);
    /* Cerrar al elegir: encadenar gestos es lo que el tope del servidor está para
       frenar, no algo que la interfaz deba facilitar. */
    setGestos(false);
    /* Sólo se borra lo escrito si era el comando que se acaba de ejecutar: quien abrió
       la tira a mitad de una frase no quiere perderla. */
    if (sugiriendo) setDraft("");
  };

  return (
    <div className={`chat${expanded ? " chat--open" : ""}`}>
      {expanded && (
        <div className="chat__log" ref={logRef}>
          {messages.length === 0 ? (
            <p className="chat__hint">Haz clic en el suelo para caminar.</p>
          ) : (
            messages.map((message) => {
              const mine = message.sessionId === selfSessionId;

              return (
                <p
                  className={`chat__line${mine ? " chat__line--mine" : ""}`}
                  key={`${message.sessionId}-${message.at}`}
                  title={timeOf(message.at)}
                >
                  <span className="chat__who">{mine ? "tú" : message.nickname}</span>
                  <span className="chat__text">{message.text}</span>
                </p>
              );
            })
          )}
        </div>
      )}

      {notice && (
        <p className="chat__notice" role="alert">
          {notice}
        </p>
      )}

      {tira.length > 0 && (
        <div className="chat__gestos" role="group" aria-label={sugiriendo ? "Comandos" : "Gestos"}>
          {tira.map((gesture, i) => (
            <button
              /* La primera es la que se lleva el Enter, y se marca: si no se ve cuál
                 es, pulsar Enter es una apuesta. */
              className={`chat__gesto${sugiriendo && i === 0 ? " chat__gesto--primero" : ""}`}
              key={gesture.id}
              type="button"
              onClick={() => elegir(gesture.id)}
            >
              <span className="chat__gesto-glifo" aria-hidden="true">
                {gesture.glyph}
              </span>
              <span className="chat__gesto-nombre">{gesture.name}</span>
              {/* El comando a la vista y no en un `title`: en un teléfono no hay dónde
                  posar el cursor, y es ahí donde más falta hace saber que existe. */}
              <span className="chat__gesto-comando">/{gesture.command}</span>
            </button>
          ))}
        </div>
      )}

      <form
        className="chat__form"
        onSubmit={(event) => {
          event.preventDefault();
          const text = draft.trim();
          if (!text) return;

          /*
           * Con la tira abierta, Enter ejecuta la primera en vez de mandar el texto.
           *
           * Sin esto, escribir "/bai" y pulsar Enter dice "/bai" en voz alta delante de
           * todos: el jugador estaba ejecutando algo, no hablando, y la interfaz le
           * acababa de enseñar una lista que sugiere justo lo contrario.
           */
          if (sugiriendo && tira.length > 0) {
            elegir(tira[0]!.id);
            return;
          }

          onSend(text);
          setDraft("");
        }}
      >
        <button
          className={`chat__gestos-abrir${gestos ? " chat__gestos-abrir--activo" : ""}`}
          type="button"
          onClick={() => setGestos((abierto) => !abierto)}
          aria-label="Gestos"
          aria-expanded={gestos}
          title="Gestos"
        >
          <span aria-hidden="true">😄</span>
        </button>

        <input
          className="chat__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={unread ? `${messages.length} mensaje(s) · escribe…` : "Escribe algo…"}
          maxLength={MAX_CHAT_LENGTH}
          aria-label="Mensaje de chat"
        />

        <button
          className="chat__send"
          type="submit"
          disabled={draft.trim().length === 0}
          aria-label="Enviar mensaje"
          title="Enviar (Enter)"
        >
          <IconEnviar />
        </button>
      </form>
    </div>
  );
}
